import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, TextField,
  IconButton, Tooltip, LinearProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import {
  Search as InspectionIcon, Visibility, CheckCircle, Cancel,
  HourglassEmpty, FactCheck,
} from '@mui/icons-material';

const API = '/api/wmc';

const resultColors: Record<string, string> = {
  pass: '#10B981',
  fail: '#EF4444',
  conditional: '#F59E0B',
  pending: '#6B7280',
};

interface Inspection {
  id: string;
  lot_number: string;
  inspection_number: string;
  inspection_type: string;
  inspector: string;
  inspection_date: string;
  result: string | null;
  doc_checklist: Record<string, boolean>;
  physical_checklist: Record<string, boolean>;
  sampling_plan: string | null;
  sample_size: number | null;
  defects_found: number;
  pmi_required: boolean;
  pmi_result: string | null;
  notes: string | null;
  completed_at: string | null;
}

const checklistLabels: Record<string, string> = {
  po_match: 'PO number matches packing slip',
  part_rev_match: 'Part number & revision match DMR',
  quantity_match: 'Quantity matches PO',
  coc_present: 'Certificate of Conformance present',
  coa_present: 'Certificate of Analysis present',
  mtr_present: 'Material Test Report present',
  mtr_na: 'MTR N/A',
  biocompat_cert: 'Biocompatibility certification',
  shelf_life_verified: 'Shelf life / expiration verified',
  supplier_change: 'No unreviewed supplier changes',
  visual_no_damage: 'Visual: No damage/contamination/corrosion',
  packaging_intact: 'Packaging integrity OK',
  labeling_correct: 'Labeling correct (P/N, Lot, Qty)',
  dimensional_pass: 'Dimensional inspection pass',
  surface_finish_pass: 'Surface finish within spec',
  cleanliness_verified: 'Cleanliness verification pass',
};

const WmcInspections: React.FC = () => {
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [resultFilter, setResultFilter] = useState('');
  const [detail, setDetail] = useState<Inspection | null>(null);

  useEffect(() => {
    loadInspections();
  }, [resultFilter]);

  const loadInspections = async () => {
    setLoading(true);
    try {
      const params = resultFilter ? `?result=${resultFilter}` : '';
      const res = await fetch(`${API}/inspections/${params}`);
      const data = await res.json();
      setInspections(data.results || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const filtered = inspections.filter(i =>
    !search ||
    i.inspection_number.toLowerCase().includes(search.toLowerCase()) ||
    i.lot_number.toLowerCase().includes(search.toLowerCase()) ||
    i.inspector.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InspectionIcon /> Inspections
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: inspections.length, color: '#3B82F6' },
          { label: 'Pass', value: inspections.filter(i => i.result === 'pass').length, color: '#10B981' },
          { label: 'Fail', value: inspections.filter(i => i.result === 'fail').length, color: '#EF4444' },
          { label: 'Pending', value: inspections.filter(i => !i.result || i.result === 'pending').length, color: '#6B7280' },
        ].map(card => (
          <Grid item xs={3} key={card.label}>
            <Card sx={{ borderLeft: `4px solid ${card.color}` }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                <Typography variant="h4" sx={{ color: card.color }}>{card.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search inspections..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ width: 300 }}
        />
        <FormControl size="small" sx={{ width: 150 }}>
          <InputLabel>Result</InputLabel>
          <Select value={resultFilter} onChange={e => setResultFilter(e.target.value)} label="Result">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pass">Pass</MenuItem>
            <MenuItem value="fail">Fail</MenuItem>
            <MenuItem value="conditional">Conditional</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} sx={{ backgroundColor: '#1F2937' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 'bold', color: '#9CA3AF', borderBottom: '2px solid #374151' } }}>
              <TableCell>Inspection #</TableCell>
              <TableCell>Lot</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Inspector</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Result</TableCell>
              <TableCell>Sampling</TableCell>
              <TableCell>Defects</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(insp => (
              <TableRow key={insp.id} hover>
                <TableCell><Typography variant="body2" fontWeight="bold">{insp.inspection_number}</Typography></TableCell>
                <TableCell>{insp.lot_number}</TableCell>
                <TableCell>
                  <Chip label={insp.inspection_type} size="small" sx={{ textTransform: 'capitalize' }} />
                </TableCell>
                <TableCell>{insp.inspector}</TableCell>
                <TableCell>{new Date(insp.inspection_date).toLocaleDateString()}</TableCell>
                <TableCell>
                  {insp.result ? (
                    <Chip
                      icon={insp.result === 'pass' ? <CheckCircle fontSize="small" /> : insp.result === 'fail' ? <Cancel fontSize="small" /> : undefined}
                      label={insp.result.toUpperCase()}
                      size="small"
                      sx={{ backgroundColor: `${resultColors[insp.result]}20`, color: resultColors[insp.result], fontWeight: 'bold' }}
                    />
                  ) : '—'}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{insp.sampling_plan || '—'}</Typography>
                  {insp.sample_size && <Typography variant="caption" color="text.secondary">n={insp.sample_size}</Typography>}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: insp.defects_found > 0 ? '#EF4444' : '#10B981', fontWeight: 'bold' }}>
                    {insp.defects_found}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => setDetail(insp)}>
                      <FactCheck fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Dialog with Checklists */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1F2937' }}>
          Inspection Report — {detail?.inspection_number}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#1F2937' }}>
          {detail && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">Lot</Typography>
                  <Typography>{detail.lot_number}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">Inspector</Typography>
                  <Typography>{detail.inspector}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">Date</Typography>
                  <Typography>{new Date(detail.inspection_date).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">Result</Typography>
                  <Chip
                    label={(detail.result || 'pending').toUpperCase()}
                    sx={{ backgroundColor: `${resultColors[detail.result || 'pending']}20`, color: resultColors[detail.result || 'pending'] }}
                  />
                </Grid>
              </Grid>

              {/* Document Checklist */}
              {Object.keys(detail.doc_checklist).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>📋 Documentation Checklist</Typography>
                  <Table size="small">
                    <TableBody>
                      {Object.entries(detail.doc_checklist).map(([key, value]) => (
                        <TableRow key={key}>
                          <TableCell sx={{ width: 40 }}>
                            {value ? <CheckCircle sx={{ color: '#10B981' }} fontSize="small" /> : <Cancel sx={{ color: '#EF4444' }} fontSize="small" />}
                          </TableCell>
                          <TableCell>{checklistLabels[key] || key}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {/* Physical Checklist */}
              {Object.keys(detail.physical_checklist).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>🔧 Physical Inspection</Typography>
                  <Table size="small">
                    <TableBody>
                      {Object.entries(detail.physical_checklist).map(([key, value]) => (
                        <TableRow key={key}>
                          <TableCell sx={{ width: 40 }}>
                            {value ? <CheckCircle sx={{ color: '#10B981' }} fontSize="small" /> : <Cancel sx={{ color: '#EF4444' }} fontSize="small" />}
                          </TableCell>
                          <TableCell>{checklistLabels[key] || key}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {detail.pmi_required && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6">🔬 PMI (Positive Material Identification)</Typography>
                  <Typography>{detail.pmi_result || 'Pending'}</Typography>
                </Box>
              )}

              {detail.notes && (
                <Box>
                  <Typography variant="h6">Notes</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{detail.notes}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1F2937' }}>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WmcInspections;
